import { useTranslation } from 'react-i18next';
import { useDebouncedCommit } from 'shared/lib/use-debounced-commit';
import { useDocumentParams } from 'app/router/params';
import { type MessageKey, i18nMaybe } from 'shared/i18n';
import { useGetDocument, DesignEntry, DesignStyle, DraftStyleEntry } from 'entities/document';
import { Spinner } from 'app/components';
import { useStyle, useStyles } from 'entities/style';
import { Dropdown } from 'app/components';
import type { DropdownItem } from 'shared/forms/Dropdown';
import { useUser } from 'app/state/contexts';
import { createNewDraft } from 'app/utils/draft';
import { useDelayedSave } from 'app/utils/hooks/delayed-save';
import { useState, useEffect, forwardRef } from 'react';
import { useDraft, useSaveDraft, NO_DRAFT } from 'entities/draft';
import { usePermissions } from 'app/api/permission-queries';
import { Chrome, type ColorResult } from '@uiw/react-color';
import Tippy from '@tippyjs/react';
import { rgbaToHex } from 'shared/lib/helper-functions';

type VariableValues = Record<string, string | null>;

const propertyTypes: Record<string, string> = {
  color: 'color',
  headingFont: 'headingFont',
  bodyFont: 'bodyFont',
  tableFont: 'tableFont',
};

const fontOptions = [
  {
    value: 'Arial',
    title: 'Arial',
  },
  {
    value: 'Courier',
    title: 'Courier',
  },
  {
    value: 'Helvetica',
    title: 'Helvetica',
  },
];

const properties: Record<string, { label: MessageKey | ''; type?: string }> = {
  xrpPrimaryColor: {
    label: 'graphical-variable:xrpPrimaryColor',
    type: propertyTypes.color,
  },
  xrpSecondaryColor: {
    label: 'graphical-variable:xrpSecondaryColor',
    type: propertyTypes.color,
  },
  xrpTertiaryColor: {
    label: 'graphical-variable:xrpTertiaryColor',
    type: propertyTypes.color,
  },
  xrpQuaternaryColor: {
    label: 'graphical-variable:xrpQuaternaryColor',
    type: propertyTypes.color,
  },
  xrpHeadingColor: {
    label: 'graphical-variable:xrpHeadingColor',
    type: propertyTypes.color,
  },
  xrpFontFamily1: {
    label: '',
  },
  xrpFontFamily2: {
    label: '',
  },
  xrpHeadingRegular: {
    label: 'graphical-variable:normal',
    type: propertyTypes.headingFont,
  },
  xrpHeadingBold: {
    label: 'graphical-variable:bold',
    type: propertyTypes.headingFont,
  },
  xrpHeadingItalic: {
    label: 'graphical-variable:italic',
    type: propertyTypes.headingFont,
  },
  xrpBodyRegular: {
    label: 'graphical-variable:normal',
    type: propertyTypes.bodyFont,
  },
  xrpBodyBold: {
    label: 'graphical-variable:bold',
    type: propertyTypes.bodyFont,
  },
  xrpBodyItalic: {
    label: 'graphical-variable:italic',
    type: propertyTypes.bodyFont,
  },
  xrpTableFont: {
    label: '',
  },
  xrpTableRegular: {
    label: 'graphical-variable:normal',
    type: propertyTypes.tableFont,
  },
  xrpTableBold: {
    label: 'graphical-variable:bold',
    type: propertyTypes.tableFont,
  },
  xrpTableItalic: {
    label: 'graphical-variable:italic',
    type: propertyTypes.tableFont,
  },
};

const defaultVariables: Record<string, string> = {
  xrpHeadingColor: '#1e2533',
  xrpPrimaryColor: '#4db894',
  xrpSecondaryColor: '#085dad',
  xrpTertiaryColor: '#e0eff9',
  xrpQuaternaryColor: '#f1f5f9',
  xrpHeadingRegular: 'Arial',
  xrpHeadingBold: 'Arial',
  xrpHeadingItalic: 'Arial',
  xrpBodyRegular: 'Arial',
  xrpBodyBold: 'Arial',
  xrpBodyItalic: 'Arial',
  xrpTableRegular: 'Arial',
  xrpTableBold: 'Arial',
  xrpTableItalic: 'Arial',
};

const propsOfType = (type: string) =>
  Object.entries(properties)
    .filter(([, v]) => v.type === type)
    .map(([p]) => p);

const colorProperties = propsOfType(propertyTypes.color);

// Treat the literal string "null" as absent — older documents may have
// been persisted with `$xrpFontFamily1: null;` which round-tripped as "null".
const isSetFontFamily = (v: unknown): v is string => !!v && v !== 'null';

const inheritedTableFont = (property: string, variables: VariableValues): string => {
  if (isSetFontFamily(variables.xrpFontFamily2)) {
    return variables.xrpFontFamily2;
  }
  const bodyEquivalent = {
    xrpTableRegular: variables.xrpBodyRegular,
    xrpTableBold: variables.xrpBodyBold,
    xrpTableItalic: variables.xrpBodyItalic,
  }[property];
  return isSetFontFamily(bodyEquivalent) ? bodyEquivalent : '';
};

export function GraphicalVariables() {
  const { t } = useTranslation('theme');

  const { documentId } = useDocumentParams();
  const { selectedOrganization } = useUser();
  const { documentPermissions } = usePermissions(documentId);
  const pDocument = useGetDocument(documentId, selectedOrganization);
  const fontIds = pDocument.data?.content.design.filter((s: DesignEntry) => s.type === 'font').map((font: DesignEntry) => font.styleId) ?? [];
  const styleId = pDocument.data?.content.design.find((d: DesignEntry) => d.type === 'graphical-variables')?.styleId;
  const style = useStyle({
    organization: selectedOrganization,
    documentRevisionId: pDocument.data?.revisionId,
    styleId,
  });
  const styleContent = (style.data as DesignStyle | undefined)?.content;
  const documentVariables = typeof styleContent === 'object' && styleContent !== null ? (styleContent as Record<string, string>) : undefined;
  const draft = useDraft({ documentSuperId: documentId });
  const draftGraphicalEntry = (draft.data as { content?: DraftStyleEntry[] } | undefined)?.content?.find(
    (s): s is Extract<DraftStyleEntry, { style: Record<string, string> | string }> => s.type === 'graphical-variables'
  );
  const draftVariables = draftGraphicalEntry && typeof draftGraphicalEntry.style === 'object' ? draftGraphicalEntry.style : undefined;
  const { mutate: saveDraft } = useSaveDraft();
  const [stateVariables, setStateVariables] = useState<VariableValues>();

  // TODO Let server deal with non-existent documentVariables
  const variables: VariableValues = { ...defaultVariables, ...(stateVariables || draftVariables || documentVariables || {}) };

  const useVariableFontForTitles = isSetFontFamily(variables.xrpFontFamily1);
  const useVariableFontForParagraphs = isSetFontFamily(variables.xrpFontFamily2);
  const useVariableFontForTables = isSetFontFamily(variables.xrpTableFont);

  const [pushDraft] = useDelayedSave((newStateVariables: VariableValues) => {
    // TODO don't overwrite changes to the scss if draft exists
    const draftEntry = {
      type: 'graphical-variables',
      style: newStateVariables,
    };

    const newDraft = createNewDraft(pDocument, (draft.data ?? NO_DRAFT) as Parameters<typeof createNewDraft>[1], draftEntry);

    saveDraft(newDraft, {
      onSuccess: () => {
        setStateVariables(undefined);
      },
    });
  }, 1000);

  const handlePropertyChange = (property: string, value: string) => {
    const newState = window.structuredClone(variables);
    newState[property] = value;

    setStateVariables(newState);
    pushDraft(newState);
  };

  const handleTitleFontToggle = () => {
    const newState = window.structuredClone(variables);
    if (useVariableFontForTitles) {
      // Keep the key present as null so downstream SCSS can still reference $xrpFontFamily1.
      newState.xrpFontFamily1 = null;
    } else {
      newState.xrpFontFamily1 = newState.xrpHeadingRegular || 'Helvetica';
    }
    setStateVariables(newState);
    pushDraft(newState);
  };

  const handleParagraphFontToggle = () => {
    const newState = window.structuredClone(variables);
    if (useVariableFontForParagraphs) {
      newState.xrpFontFamily2 = null;
    } else {
      newState.xrpFontFamily2 = newState.xrpBodyRegular || 'Helvetica';
    }
    setStateVariables(newState);
    pushDraft(newState);
  };

  const handleTableFontToggle = () => {
    const newState = window.structuredClone(variables);
    if (useVariableFontForTables) {
      newState.xrpTableFont = null;
    } else {
      newState.xrpTableFont = newState.xrpTableRegular || newState.xrpBodyRegular || 'Helvetica';
    }
    setStateVariables(newState);
    pushDraft(newState);
  };

  return (
    <div>
      <div className="header-toggle-wrapper" style={{ display: 'flex', alignItems: 'center' }}>
        <h2 className="mb-3" style={{ margin: 0 }}>
          {t('graphical-variables.headers')}
        </h2>

        {documentPermissions.includes('CODER') && (
          <div className="form-check" style={{ marginLeft: 'auto', justifyContent: 'center', alignItems: 'center' }}>
            <input
              className="form-check-input"
              type="checkbox"
              id="toggleVariableFontForHeaders"
              checked={useVariableFontForTitles}
              onChange={handleTitleFontToggle}
            />
            <label className="form-check-label" htmlFor="toggleVariableFont">
              {t('graphical-variables.toggle-variable-fonts')}
            </label>
          </div>
        )}
      </div>

      {useVariableFontForTitles ? (
        <div className="d-flex flex-row gap-r-1">
          <FontPicker
            key={'xrpFontFamily1'}
            font={variables['xrpFontFamily1']}
            property={'xrpFontFamily1'}
            onChange={handlePropertyChange}
            fontIds={fontIds}
            documentRevisionId={pDocument.data?.revisionId}
          />
        </div>
      ) : (
        <div className="d-flex flex-row gap-r-1">
          {propsOfType(propertyTypes.headingFont).map((property) => (
            <FontPicker
              key={property}
              font={variables[property]}
              property={property}
              onChange={handlePropertyChange}
              fontIds={fontIds}
              documentRevisionId={pDocument.data?.revisionId}
            />
          ))}
        </div>
      )}

      <div className="mb-5"></div>

      <div className="header-toggle-wrapper" style={{ display: 'flex', alignItems: 'center' }}>
        <h2 className="mb-3" style={{ margin: 0 }}>
          {t('graphical-variables.body')}
        </h2>

        {documentPermissions.includes('CODER') && (
          <div className="form-check" style={{ marginLeft: 'auto', justifyContent: 'center', alignItems: 'center' }}>
            <input
              className="form-check-input"
              type="checkbox"
              id="toggleVariableFontForParagraphs"
              checked={useVariableFontForParagraphs}
              onChange={handleParagraphFontToggle}
            />
            <label className="form-check-label" htmlFor="toggleVariableFont">
              {t('graphical-variables.toggle-variable-fonts')}
            </label>
          </div>
        )}
      </div>

      {useVariableFontForParagraphs ? (
        <div className="d-flex flex-row gap-r-1">
          <FontPicker
            key={'xrpFontFamily2'}
            font={variables['xrpFontFamily2']}
            property={'xrpFontFamily2'}
            onChange={handlePropertyChange}
            fontIds={fontIds}
            documentRevisionId={pDocument.data?.revisionId}
          />
        </div>
      ) : (
        <div className="d-flex flex-row gap-r-1 ">
          {propsOfType(propertyTypes.bodyFont).map((property) => (
            <FontPicker
              key={property}
              font={variables[property]}
              property={property}
              onChange={handlePropertyChange}
              fontIds={fontIds}
              documentRevisionId={pDocument.data?.revisionId}
            />
          ))}
        </div>
      )}

      <div className="mb-5"></div>

      <div className="header-toggle-wrapper" style={{ display: 'flex', alignItems: 'center' }}>
        <h2 className="mb-3" style={{ margin: 0 }}>
          {t('graphical-variables.tables')}
        </h2>

        {documentPermissions.includes('CODER') && (
          <div className="form-check" style={{ marginLeft: 'auto', justifyContent: 'center', alignItems: 'center' }}>
            <input
              className="form-check-input"
              type="checkbox"
              id="toggleVariableFontForTables"
              checked={useVariableFontForTables}
              onChange={handleTableFontToggle}
            />
            <label className="form-check-label" htmlFor="toggleVariableFontForTables">
              {t('graphical-variables.toggle-variable-fonts')}
            </label>
          </div>
        )}
      </div>

      {useVariableFontForTables ? (
        <div className="d-flex flex-row gap-r-1">
          <FontPicker
            key={'xrpTableFont'}
            font={variables['xrpTableFont']}
            property={'xrpTableFont'}
            onChange={handlePropertyChange}
            fontIds={fontIds}
            documentRevisionId={pDocument.data?.revisionId}
          />
        </div>
      ) : (
        <div className="d-flex flex-row gap-r-1">
          {propsOfType(propertyTypes.tableFont).map((property) => (
            <FontPicker
              key={property}
              font={variables[property]}
              property={property}
              onChange={handlePropertyChange}
              fontIds={fontIds}
              documentRevisionId={pDocument.data?.revisionId}
              placeHolder={inheritedTableFont(property, variables)}
            />
          ))}
        </div>
      )}
      <hr className="my-5 border-gray-300" />
      <div className="d-flex flex-row gap-r-1">
        {colorProperties.map((property) => (
          <Tippy
            content={
              <ColorPicker
                defaultValue={variables[property] ?? ''}
                property={property}
                onChangeCallBack={(hex: string) => {
                  handlePropertyChange(property, hex);
                }}
              />
            }
            placement="bottom"
            theme="light"
            interactive={true}
            trigger="click"
            appendTo="parent"
            key={property}
          >
            <div className={`border border-lined border-blue-300 rounded bg-white p-2 d-flex flex-column`} style={{ width: '149px' }}>
              <div className="all-unset d-block w-100 flex-grow-1 aspect-13x8 mb-2" style={{ backgroundColor: variables[property] ?? undefined }} />
              <div className="text-blue-700 fw-bold fs-7 mb-1">{i18nMaybe(properties[property].label)}</div>
              <div className="text-gray-800">{variables[property]}</div>
            </div>
          </Tippy>
        ))}
      </div>
    </div>
  );
}

interface ColorPickerProps {
  defaultValue: string;
  onChangeCallBack: (hex: string) => void;
  property: string;
}

const ColorPicker = forwardRef<HTMLDivElement, ColorPickerProps>(({ defaultValue, onChangeCallBack, property }, ref) => {
  const [color, setColor] = useState(defaultValue);
  useEffect(() => {
    setColor(defaultValue);
  }, [defaultValue]);
  const commitColor = useDebouncedCommit(onChangeCallBack);
  return (
    <div ref={ref}>
      <Chrome
        key={property}
        color={color || '#ffffff'}
        onChange={(color: ColorResult) => {
          const hex = rgbaToHex({ ...color.rgba });
          setColor(hex);
          commitColor(hex);
        }}
      />
    </div>
  );
});

const FontItem = ({ item }: { item: DropdownItem }) => {
  return (
    <div className="page-wrapper force-text" style={{ fontFamily: item.value as string }}>
      <div>{item.title}</div>
    </div>
  );
};

interface FontPickerProps {
  font: unknown;
  property: string;
  onChange: (property: string, value: string) => void;
  fontIds: string[];
  documentRevisionId?: string;
  placeHolder?: string;
}

function FontPicker(props: FontPickerProps) {
  const { selectedOrganization } = useUser();
  const { font, property, onChange, fontIds, documentRevisionId, placeHolder } = props;
  const styleQuery = useStyles({ styleIds: fontIds, organization: selectedOrganization, documentRevisionId });

  const handleFontChange = (value: unknown) => {
    return onChange(property, value as string);
  };

  if (styleQuery.isLoading) {
    return <Spinner />;
  }

  const customFonts =
    (styleQuery.data as { content: string }[] | undefined)
      ?.map((style) => extractFontName(style.content))
      .filter((fontName): fontName is string => !!fontName)
      .map((fontName) => ({ value: fontName, title: fontName })) ?? [];

  return (
    <div className="flex-grow-1">
      <label className="form-label">{i18nMaybe(properties[property].label)}</label>
      <Dropdown items={[...fontOptions, ...customFonts]} value={font} onChange={handleFontChange} wrapper={FontItem} placeHolder={placeHolder} />
    </div>
  );
}

function extractFontName(style: string): string | undefined {
  const fontFamilyRegex = /font-family:\s*['"]([^'"]+)['"]/;
  return style.match(fontFamilyRegex)?.[1];
}
